import { computed, nextTick, ref } from "vue";
import {
  claimRateUsPromptApi,
  markRateUsPromptShownApi,
} from "@/api/GepService";

export type RateUsPromptTrigger = "TRANSACTION" | "ENGAGEMENT";

const isOpen = ref(false);
const isManual = ref(false);
const pendingTrigger = ref<RateUsPromptTrigger | null>(null);
const claimedTrigger = ref<RateUsPromptTrigger | null>(null);
const claimedClaimId = ref<string | null>(null);
const claimedExpiresAt = ref<number | null>(null);
const displayedClaimId = ref<string | null>(null);
const claiming = ref(false);
const blockers = ref<Record<string, boolean>>({});
const preparedCustomerDetails = ref<DynamicValue>(null);
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let operationGeneration = 0;

const triggerPriority: Record<RateUsPromptTrigger, number> = {
  ENGAGEMENT: 1,
  TRANSACTION: 2,
};

export function queueRateUsTrigger(trigger: RateUsPromptTrigger) {
  if (
    !pendingTrigger.value ||
    triggerPriority[trigger] > triggerPriority[pendingTrigger.value]
  ) {
    pendingTrigger.value = trigger;
  }
}

export function recordRateUsBlocker(name: string, blocked: boolean) {
  blockers.value = { ...blockers.value, [name]: blocked };
}

export function resetRateUsPromptState() {
  operationGeneration += 1;
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = undefined;
  isOpen.value = false;
  isManual.value = false;
  pendingTrigger.value = null;
  claimedTrigger.value = null;
  claimedClaimId.value = null;
  claimedExpiresAt.value = null;
  displayedClaimId.value = null;
  claiming.value = false;
  blockers.value = {};
  preparedCustomerDetails.value = null;
}

export async function processRateUsPrompt(params: {
  token: string;
  isSafe: () => boolean;
  prepare: () => Promise<DynamicValue>;
}) {
  if (
    !params.token ||
    claiming.value ||
    isOpen.value ||
    displayedClaimId.value ||
    !params.isSafe()
  ) {
    return;
  }

  const generation = operationGeneration;

  if (
    claimedTrigger.value &&
    pendingTrigger.value &&
    triggerPriority[pendingTrigger.value] >
      triggerPriority[claimedTrigger.value]
  ) {
    const supersededTrigger = pendingTrigger.value;
    claimedTrigger.value = null;
    claimedClaimId.value = null;
    claimedExpiresAt.value = null;
    preparedCustomerDetails.value = null;
    pendingTrigger.value = supersededTrigger;
  }

  if (claimedTrigger.value && claimedClaimId.value) {
    claiming.value = true;
    try {
      if (
        claimedExpiresAt.value !== null &&
        Date.now() >= claimedExpiresAt.value
      ) {
        const expiredTrigger = claimedTrigger.value;
        claimedTrigger.value = null;
        claimedClaimId.value = null;
        claimedExpiresAt.value = null;
        preparedCustomerDetails.value = null;
        queueRateUsTrigger(expiredTrigger);
        queueMicrotask(() => void processRateUsPrompt(params));
        return;
      }
      if (!preparedCustomerDetails.value) {
        preparedCustomerDetails.value = await params.prepare();
      }
      if (
        generation !== operationGeneration ||
        isOpen.value ||
        !params.isSafe()
      ) {
        return;
      }

      const claimId = claimedClaimId.value;
      displayedClaimId.value = claimId;
      claimedTrigger.value = null;
      claimedClaimId.value = null;
      claimedExpiresAt.value = null;
      isManual.value = false;
      isOpen.value = true;
      await nextTick();
      if (generation === operationGeneration) {
        void confirmDisplayedRateUsPrompt(params, generation, claimId);
      }
    } catch (error) {
      if (generation !== operationGeneration || isOpen.value) return;
      if (!retryTimer) {
        retryTimer = setTimeout(() => {
          retryTimer = undefined;
          void processRateUsPrompt(params);
        }, 10_000);
      }
      console.warn("Unable to confirm proactive Rate Us display", error);
      return;
    } finally {
      claiming.value = false;
    }
    return;
  }

  const trigger = pendingTrigger.value;
  if (!trigger) return;

  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = undefined;
  }

  claiming.value = true;
  pendingTrigger.value = null;
  try {
    const result = await claimRateUsPromptApi({
      token: params.token,
      trigger,
    });
    if (generation !== operationGeneration || isOpen.value) return;
    if (!result?.claimed || !result?.claimId) return;

    claimedTrigger.value = trigger;
    claimedClaimId.value = result.claimId;
    const expiresAt = Date.parse(String(result.claimExpiresAt || ""));
    claimedExpiresAt.value = Number.isNaN(expiresAt) ? null : expiresAt;
    if (
      pendingTrigger.value &&
      triggerPriority[pendingTrigger.value] > triggerPriority[trigger]
    ) {
      claimedTrigger.value = null;
      claimedClaimId.value = null;
      claimedExpiresAt.value = null;
      queueMicrotask(() => void processRateUsPrompt(params));
      return;
    }
    if (params.isSafe()) {
      queueMicrotask(() => void processRateUsPrompt(params));
    }
  } catch (error) {
    if (generation !== operationGeneration || isOpen.value) return;
    queueRateUsTrigger(trigger);
    retryTimer = setTimeout(() => {
      retryTimer = undefined;
      void processRateUsPrompt(params);
    }, 10_000);
    console.warn("Unable to claim proactive Rate Us prompt", error);
  } finally {
    claiming.value = false;
  }
}

async function confirmDisplayedRateUsPrompt(
  params: {
    token: string;
    isSafe: () => boolean;
    prepare: () => Promise<DynamicValue>;
  },
  generation: number,
  claimId: string,
) {
  if (
    generation !== operationGeneration ||
    displayedClaimId.value !== claimId
  ) {
    return;
  }

  try {
    await markRateUsPromptShownApi({
      token: params.token,
      claimId,
    });
    if (
      generation !== operationGeneration ||
      displayedClaimId.value !== claimId
    ) {
      return;
    }
    displayedClaimId.value = null;
  } catch (error) {
    if (
      generation !== operationGeneration ||
      displayedClaimId.value !== claimId
    ) {
      return;
    }
    if (!retryTimer) {
      retryTimer = setTimeout(() => {
        retryTimer = undefined;
        void confirmDisplayedRateUsPrompt(params, generation, claimId);
      }, 10_000);
    }
    console.warn("Unable to confirm proactive Rate Us display", error);
  }
}

export function useRateUsPrompt() {
  return {
    isOpen,
    isManual,
    pendingTrigger,
    claimedTrigger,
    claimedClaimId,
    claimedExpiresAt,
    displayedClaimId,
    claiming,
    preparedCustomerDetails,
    isBlocked: computed(() => Object.values(blockers.value).some(Boolean)),
    openManual: () => {
      operationGeneration += 1;
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = undefined;
      pendingTrigger.value = null;
      claimedTrigger.value = null;
      claimedClaimId.value = null;
      claimedExpiresAt.value = null;
      displayedClaimId.value = null;
      preparedCustomerDetails.value = null;
      isManual.value = true;
      isOpen.value = true;
    },
    close: () => {
      isOpen.value = false;
      isManual.value = false;
      preparedCustomerDetails.value = null;
    },
  };
}
